import { useCallback, useEffect, useMemo, useState } from "react";
import type { ITransactionImport, TransactionImportType, TransactionOrigin } from "@types";
import { getTransactionImports } from "@services/form";
import { useAppSelector } from "@store";
import { getUserAccount } from "@store/login/LoginSlice";
import type { TransactionListOriginFilters } from "./useTransactionListData";

export const TRANSACTION_ORIGINS: TransactionOrigin[] = ["FORM", "IMPORT", "INTEGRATION"];

export const useTransactionOriginFilters = (type: TransactionImportType) => {
  const accountId = useAppSelector(getUserAccount);
  const [origins, setOrigins] = useState<TransactionOrigin[]>([]);
  const [importIds, setImportIds] = useState<string[]>([]);
  const [imports, setImports] = useState<ITransactionImport[]>([]);
  const [isLoadingImports, setIsLoadingImports] = useState(false);

  const reloadImports = useCallback(async () => {
    setIsLoadingImports(true);
    try {
      setImports(await getTransactionImports(type, 0, 100, accountId));
    } catch {
      setImports([]);
    } finally {
      setIsLoadingImports(false);
    }
  }, [accountId, type]);

  useEffect(() => {
    reloadImports();
  }, [reloadImports]);

  const originFilters = useMemo<TransactionListOriginFilters>(
    () => ({
      ...(origins.length > 0 && { origins }),
      ...(importIds.length > 0 && { importIds }),
    }),
    [origins, importIds]
  );

  return {
    origins,
    importIds,
    imports,
    isLoadingImports,
    originFilters,
    setOrigins,
    setImportIds,
    reloadImports,
  };
};

export type TransactionOriginFiltersState = ReturnType<typeof useTransactionOriginFilters>;
